import { HAS_WINDOW } from '@eventbrite/feature-detection';
import { setWindowLocation } from '@eventbrite/http';
import { gettext } from '@eventbrite/i18n';
import {
    getLocationData,
    locationDuck,
    setLocationData,
    storeQuery,
} from '@eventbrite/redux-destination';
import * as analytics from '@eventbrite/site-analytics';
import {
    deepKeysToCamel,
    keysCamelToSnake,
} from '@eventbrite/transformation-utils';
import { $FixMe } from '@eventbrite/ts-utils';
import * as Sentry from '@sentry/browser';
import compact from 'lodash/compact';
import each from 'lodash/each';
import filter from 'lodash/filter';
import includes from 'lodash/includes';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import size from 'lodash/size';
import uniq from 'lodash/uniq';
import { StringFlag } from 'meow';
import {
    ALL_EVENTS_TYPE,
    CATEGORY_TAG_TYPE,
    CURRENT_FUTURE,
    CUSTOM_DATE,
    EVENTS_TYPE,
    EVENT_PAGE_PARAM,
    FORMAT_TAG_TYPE,
    LOCATION_STORAGE_KEY,
} from '../../constants';
import {
    GA_LOCATION_CHANGE_ACTION,
    GA_REDO_FROM_MAP,
    GA_SEARCH_CATEGORY,
    GA_VIEW_EVENT_MAP_VIEW,
    SEARCH_EVENT_NAVIGATION,
} from '../../constants/analytics';
import { CLEAR_PRICE_FILTER } from '../../constants/filters';
import {
    bboxShape,
    EventSearch,
    PointRadius,
    Suggestions,
} from '../../types/index';
import {
    getSearchEventHeaderTitle,
    updateDocumentTitle,
} from '../../utils/general';
import { transformSearchBucketContent } from '../../utils/transformation';
import { logDiscoveryRequest } from '../api/analytics';
import { getPlaceFromRequest as getPlaceFromRequestApi } from '../api/location';
import {
    search as searchApi,
    searchAutocompleteSuggestions as searchAutocompleteSuggestionsApi,
    searchEventSuggestions as searchEventSuggestionsApi,
} from '../api/search';
import { getPathWithSanitizedQueryParams } from '../selectors/url';
import { getFilterEventTrackingData } from '../utils/analytics';
import {
    parseSuggestions,
    transformSearchIntoSuggestions,
} from '../utils/suggestions';
import { updateContent } from './content';
import { updateSearchLocation } from './location';
import { getFuturePage, resetPagination, setCurrentPage } from './pagination';
import { showErrorNotification } from './ui';

const { trackEventFromState } = analytics;
const {
    actions: { updateLocation },
} = locationDuck;

export const UPDATE_SEARCH_CRITERIA = 'UPDATE_SEARCH_CRITERIA';
export const updateSearchCriteria = (
    searchQuery: Omit<EventSearch, 'bbox'>,
) => ({
    type: UPDATE_SEARCH_CRITERIA,
    payload: {
        eventSearch: searchQuery,
    },
});

const SEARCH_RADIUS_DEFAULTS = {
    radius: '150km',
    offset: '15km',
    scale: '15km',
};

export const removeBBoxFromSearchCriteria = (eventSearch: EventSearch) =>
    updateSearchCriteria(omit(eventSearch, ['bbox']));

export const countUnattendableEvents = (searchResponse: any) => {
    return searchResponse.events.results.reduce(
        (
            prev: number,
            event: {
                event_sales_status: { sales_status: string };
                num_children: number;
            },
        ) => {
            let isAttendable = true;

            if (event.event_sales_status.sales_status !== 'on_sale') {
                isAttendable = false;
            }

            // Series events should always be considered attendable
            if (event.num_children > 1) {
                isAttendable = true;
            }

            return isAttendable ? prev : prev + 1;
        },
        0,
    );
};

/*
Helper which performs a search action with novel data, clearing
any existing pagination state. And updates route logic to represent
new search that was performed.
*/
const _doNewSearch =
    (
        history: object,
        searchQuery: EventSearch,
        gaTrackingData: any[] = [],
        shouldUpdateRoute = true,
    ) =>
    (dispatch: Function, getState: Function) => {
        const state: {
            search: { eventSearch: EventSearch };
            pagination: object;
            app: {
                guestId?: string;
                publicId?: string;
            };
            location: {
                currentPlace: string;
                currentPlaceParent: string;
                placeType: string;
                slug: string;
            };
            admin: { experimentOverride?: any };
            user: { isStaff?: boolean };
            content?: JSX.Element;
        } = getState();
        const {
            search: { eventSearch },
            pagination,
            app: { guestId, publicId },
            location: { currentPlace, currentPlaceParent, placeType, slug },
            admin: { experimentOverride } = {},
            user: { isStaff } = {},
            content: oldContent,
        } = state;
        dispatch(resetPagination());

        // update page title with new search query data
        updateDocumentTitle(
            getSearchEventHeaderTitle(
                { currentPlace, currentPlaceParent, placeType },
                { eventSearch: searchQuery },
                true,
            ),
        );

        // search optimistic update
        dispatch(updateSearchCriteria(searchQuery));

        // location optimistic update
        const queryParamsToOmit = [EVENT_PAGE_PARAM];

        const searchTime = new Date().getTime();
        let searchResponse: any;

        return searchApi({ searchQuery, experimentOverride, isStaff, history })
            .then((response: any) => {
                searchResponse = response;

                return dispatch(
                    updateContent(transformSearchBucketContent(response)),
                );
            })
            .then(() => dispatch(setCurrentPage(0)))
            .then(() => {
                let locationSlug: $FixMe = null;

                if (
                    eventSearch.places &&
                    searchQuery.places &&
                    eventSearch.places[0] === searchQuery.places[0]
                ) {
                    locationSlug = slug;
                }

                const searchId = logDiscoveryRequest({
                    eventSearch: searchQuery,
                    guestId,
                    locationSlug,
                    publicUserId: publicId,
                    userGenerated: true,
                    navigation: SEARCH_EVENT_NAVIGATION,
                    action: gaTrackingData
                        .map((d: { action: string }) => d.action)
                        .sort()
                        .join(','),
                    // variant can be used to track experiments
                    variant: null,
                });

                const searchResponseTime = new Date().getTime() - searchTime;

                const searchResultCount = searchResponse
                    ? searchResponse.events.results.length
                    : 0;

                let metricsToTrack = {
                    searchQueryResponseTime: searchResponseTime,
                    searchResultCount,
                    searchResultCountUnattendable: null,
                };

                // Don't track unattendable if we don't yet have the event sales status
                const shouldTrackUnattendableEventCount =
                    searchResultCount &&
                    searchResponse &&
                    searchResponse.events.results[0].event_sales_status;

                if (shouldTrackUnattendableEventCount) {
                    const unattendableEventCount =
                        countUnattendableEvents(searchResponse);

                    metricsToTrack = {
                        ...metricsToTrack,
                        searchResultCountUnattendable: unattendableEventCount,
                    };
                }

                if (size(gaTrackingData)) {
                    each(gaTrackingData, (gaData: { dimensions: object }) => {
                        if (searchId) {
                            trackEventFromState(state, {
                                ...gaData,
                                dimensions: {
                                    ...gaData.dimensions,
                                    searchId,
                                },
                                metrics: metricsToTrack,
                            });
                        } else {
                            trackEventFromState(state, gaData);
                        }
                    });
                }
            })
            .then(() => {
                if (shouldUpdateRoute) {
                    dispatch(
                        updateSearchLocation(history, { queryParamsToOmit }),
                    );
                }
            })
            .catch((e: unknown) => {
                if (typeof Response !== 'undefined' && e instanceof Response) {
                    if (e.status < 500) {
                        Sentry.captureException(e.statusText, {
                            extra: {
                                search_body: searchQuery,
                                status_code: e.status,
                            },
                        });
                    }
                } else {
                    Sentry.captureException(e, {
                        extra: { search_body: searchQuery },
                    });
                }

                dispatch(
                    showErrorNotification(
                        gettext('Error finding events. Please try again'),
                    ),
                );

                dispatch(
                    updateContent({
                        ...oldContent,
                        pagination,
                        searchCriterias: { eventSearch },
                    }),
                );
            });
    };

const _getAndSetCurrentPageFromMostRecentSearch =
    () => (dispatch: Function, getState: Function) => {
        const {
            search: {
                eventSearch: { page },
            },
        } = getState();

        return dispatch(setCurrentPage(page - 1));
    };

/*
Similar to _doNewSearch but does so without updating or modifying
any of the route information. This is used for handling browser
back/forward cases so that our displayed content is accurate to the already
present URL
*/
const _doSearchWithoutRouteUpdate =
    (searchQuery: EventSearch) => (dispatch: Function, getState: Function) => {
        //Compare the eventSearch object in state with the search query
        //coming in here. If the only thing changing is pagination param
        //and we have associated entities don't run full search and
        //don't reset pagination etc. Otherwise, _do_ reset
        //the pagination objet in state.
        const {
            search: { eventSearch },
            pagination: { event },
            location: { currentPlace, currentPlaceParent, placeType },
        } = getState();

        const paginationKeys = ['continuation', 'page'];
        const paginationOnlyChange = isEqual(
            omit(eventSearch, paginationKeys),
            omit(searchQuery, paginationKeys),
        );
        const zeroBasedPage = (searchQuery.page || 1) - 1;

        /* The user navigated with the browser between paginated pages so
        just show associated results if we have the data */
        if (paginationOnlyChange && event[zeroBasedPage]) {
            dispatch(setCurrentPage(zeroBasedPage));
            return dispatch(updateSearchCriteria(searchQuery));
        }

        /* If a new search was ran between these state, reset the pagination store */
        if (!paginationOnlyChange) {
            dispatch(resetPagination());
        }

        // update page title with new search query data
        updateDocumentTitle(
            getSearchEventHeaderTitle(
                { currentPlace, currentPlaceParent, placeType },
                { eventSearch: searchQuery },
                true,
            ),
        );

        dispatch(updateSearchCriteria(searchQuery));
        dispatch(getFuturePage());

        /* In the case that we don't already have the entities in the store, re-run the search */
        return searchApi({ searchQuery })
            .then((response: any) =>
                dispatch(updateContent(transformSearchBucketContent(response))),
            )
            .then(() => dispatch(_getAndSetCurrentPageFromMostRecentSearch()))
            .catch(() => {
                dispatch(
                    showErrorNotification(
                        gettext(
                            'Unable to get events. Please refresh the page.',
                        ),
                    ),
                );
            });
    };

export const UPDATE_SEARCH_SUGGESTIONS = 'UPDATE_SEARCH_SUGGESTIONS';
export const SUGGESTIONS_LOADING = 'SUGGESTIONS_LOADING';
const updateSearchAutocompleteSuggestions = (suggestions?: Suggestions) => ({
    type: UPDATE_SEARCH_SUGGESTIONS,
    payload: suggestions,
});

export const searchAutocompleteSuggestions =
    (query?: string) => (dispatch: Function, getState?: Function) => {
        if (!query) {
            return dispatch(updateSearchAutocompleteSuggestions([]));
        }

        const {
            location: { placeId, isOnline },
        } = getState?.();

        dispatch({ type: SUGGESTIONS_LOADING });

        return searchAutocompleteSuggestionsApi({
            placeId,
            onlineEventsOnly: isOnline,
            query,
        })
            .then((response: any) =>
                dispatch(
                    updateSearchAutocompleteSuggestions(
                        parseSuggestions(response),
                    ),
                ),
            )
            .catch(() => {
                dispatch(
                    showErrorNotification(
                        gettext(
                            'Error finding events. Please refresh and try again',
                        ),
                    ),
                );
            });
    };

export const searchEventSuggestions =
    (forceOnline = false) =>
    (dispatch: Function, getState: Function) => {
        const {
            location: { placeId, isOnline },
        } = getState();
        dispatch({ type: SUGGESTIONS_LOADING });
        return searchEventSuggestionsApi({
            placeId: forceOnline ? undefined : placeId,
            onlineEventsOnly: forceOnline || isOnline,
        })
            .then((response: any) =>
                dispatch(
                    updateSearchAutocompleteSuggestions(
                        parseSuggestions(
                            transformSearchIntoSuggestions(response),
                        ),
                    ),
                ),
            )
            .catch(() => {
                //do nothing since this happens behind the scenes to
                //pre-populate the event suggestion dropdown.
            });
    };

export const updateSearchInformationFromUrl =
    (path: string, queryParams?: object) =>
    (dispatch: Function, getState: Function) => {
        const { locationAndSearchInformation } = getState();

        // Construct path using same util to set key
        const constructedPath = getPathWithSanitizedQueryParams(
            path,
            queryParams || {},
        );

        // get search data from state that matches with the given path and perform a search
        const data = locationAndSearchInformation[constructedPath];

        if (data) {
            const {
                search: { eventSearch },
                location: locationFromState,
            } = data;

            dispatch(updateLocation(locationFromState));

            dispatch(_doSearchWithoutRouteUpdate(eventSearch));
        } else {
            // If we somehow get into a state where we
            // don't have the associated URL in our store of searches,
            // reload the page to hit and server and get appropriate data

            // Until the API supports url slugs in place of place-ids (or place-ids are
            // ingrained) it is impossible to resolve the place id from a location on the
            // client. So we must hit the server.
            dispatch(getFuturePage());
            setWindowLocation(constructedPath);
        }
    };

export const handleRemoveOnlineFilter =
    (history: object, _args: $FixMe) =>
    (dispatch: Function, getstate: Function) => {
        const { location } = getstate();

        if (location.fallBack) {
            dispatch(
                searchInformationForPlace({
                    ...location.fallBack,
                    history,
                }),
            );
        } else {
            // The user arrived to an `online` search or browse page directly.
            // So we make a best guess based on the available information:

            //1. Cookie with location data is stored
            //2. IP lookup to get a best guess
            const locationData = getLocationData();
            if (locationData && locationData.placeId) {
                dispatch(
                    searchInformationForPlace({
                        ...locationData,
                        isOnline: false,
                        history,
                    }),
                );
            } else {
                getPlaceFromRequestApi()
                    .then(({ place }: { place: string }) => {
                        dispatch(
                            searchInformationForPlace({
                                ...deepKeysToCamel(place),
                                isOnline: false,
                                history,
                            }),
                        );
                    })
                    .catch(() => {
                        showErrorNotification(
                            gettext(
                                'Unable to find your current location. Please select one',
                            ),
                        );
                    });
            }
        }
    };

interface searchInformationForPlaceProps {
    slug?: string;
    placeId?: string;
    latitude?: number;
    longitude?: number;
    placeType?: string;
    currentPlace?: string;
    currentPlaceParent?: string;
    isUsingCurrentLocation?: boolean;
    isOnline?: boolean;
    history?: object;
    userLatLng?: GeolocationCoordinates;
}

export const searchInformationForPlace =
    (
        {
            slug,
            placeId,
            latitude,
            longitude,
            placeType,
            currentPlace,
            currentPlaceParent,
            isUsingCurrentLocation,
            isOnline = false,
            history,
            userLatLng,
        }: searchInformationForPlaceProps,
        query?: string,
    ) =>
    (dispatch: Function, getState: Function) => {
        dispatch(removeBBoxFromSearchCriteria(getState().search.eventSearch));
        const {
            search: { eventSearch },
            location: oldLocation,
        } = getState();

        const label = `${oldLocation.currentPlace}--${currentPlace}`;

        const gaTrackingData = {
            label,
            category: GA_SEARCH_CATEGORY,
            action: GA_LOCATION_CHANGE_ACTION,
        };

        const locationData = {
            slug,
            placeId,
            latitude,
            longitude,
            placeType,
            currentPlace,
            currentPlaceParent,
            isUsingCurrentLocation,
            isOnline,
            userLatLng,
        };

        dispatch({
            type: 'SET_FALLBACK_LOCATION',
            payload: oldLocation,
        });

        dispatch(updateLocation(locationData));

        // See https://docs.google.com/document/d/1Vg-kyxM34gw71vw_5dLcfzFRhzM3scQ3FKtYUlWXLSA/edit?usp=sharing
        placeId && setLocationData(keysCamelToSnake(locationData));

        /* When the user selects "online" from the Recent suggestions the placeId is null and the slug is online,
        but the isOnline is false. Because of that we should change it to reflect the correct filter */
        if (!placeId && slug === 'online') {
            locationData.isOnline = true;
        }

        if (HAS_WINDOW && !locationData.isOnline && !!placeId) {
            /*
             * Stores the current place data in the localStorage.
             * To store an object we have to replace the `,` with `;`
             * because we later reconstruct the data separating different
             * locations by `,` and the object itself should not be separated
             * until parsed as a JSON object.
             */
            storeQuery(
                JSON.stringify({
                    currentPlace,
                    currentPlaceParent,
                    content: currentPlace,
                    secondaryContent: currentPlaceParent,
                    value: `recent-${placeId}`,
                    latitude,
                    longitude,
                    placeType,
                    placeId,
                    slug,
                    isOnline,
                    isHistorySuggestion: true,
                }).replace(/,/g, ';'),
                LOCATION_STORAGE_KEY,
            );
        }

        dispatch(searchEventSuggestions());

        const isPointRadiusActive = !!Object.keys(eventSearch.pointRadius)
            .length;

        const searchQuery = {
            ...eventSearch,
            q: !(!query && query !== '') ? query : eventSearch.q,
            page: 1,
            onlineEventsOnly: isOnline,
            places: isOnline ? [] : [placeId],
            pointRadius: isOnline
                ? {}
                : {
                      ...eventSearch.pointRadius,
                      ...(isPointRadiusActive ? { latitude, longitude } : {}),
                      ...(isUsingCurrentLocation && userLatLng
                          ? {
                                latitude: userLatLng.latitude,
                                longitude: userLatLng.longitude,
                            }
                          : {}),
                      ...(isUsingCurrentLocation &&
                      userLatLng &&
                      !isPointRadiusActive
                          ? {
                                ...SEARCH_RADIUS_DEFAULTS,
                            }
                          : {}),
                  },
        };

        dispatch(_doNewSearch(history || {}, searchQuery, [gaTrackingData]));
    };

export const cleanLongitude = (longitude: number) =>
    (((longitude % 360) + 540) % 360) - 180;

export const searchByBBox =
    (history?: object, bbox?: bboxShape) =>
    (dispatch: Function, getState: Function) => {
        const state = getState();
        const {
            search: { eventSearch },
            location: { slug },
        } = state;

        // Create bounding box string of shape: '${left},${bottom},${right},${top}'
        const bboxParam = `${cleanLongitude(bbox?.sw?.lng || 0)},${
            bbox?.sw?.lat
        },${cleanLongitude(bbox?.ne?.lng || 0)},${bbox?.ne?.lat}`;

        const searchQuery: EventSearch = {
            ...eventSearch,
            pointRadius: {},
            onlineEventsOnly: false,
            bbox: bboxParam,
        };

        const gaTrackingData = {
            label: slug,
            category: GA_SEARCH_CATEGORY,
            action: GA_REDO_FROM_MAP,
        };

        dispatch(
            _doNewSearch(history || {}, omit(searchQuery, ['places']), [
                gaTrackingData,
            ]),
        );
    };

// If a bounding box is available, use it.  If not, search by place
export const searchByBBoxOrPlace =
    (history: object, place: { bbox: bboxShape }, query?: string) =>
    (dispatch: Function) => {
        if ('bbox' in place) {
            dispatch(searchByBBox(history, place.bbox));
        } else {
            dispatch(searchInformationForPlace({ ...place, history }, query));
        }
    };

export const viewEventMapView =
    (eventId: string) => (_: any, getState: Function) => {
        const state = getState();

        trackEventFromState(state, {
            label: eventId,
            category: GA_SEARCH_CATEGORY,
            action: GA_VIEW_EVENT_MAP_VIEW,
        });
    };

const _isTagType = (tag: string, type: string) => tag.indexOf(type) > -1;

const _getUpdatedTags = (
    currentTags: string[],
    tag?: string,
    format?: string,
) => {
    //Return current tags if no tag or format passed
    if (tag === undefined && format === undefined) {
        return currentTags;
    }

    let updatedTags = [...currentTags];

    if (tag || tag === '') {
        //updated tags should equal the new array minus the previously selected category
        updatedTags = [
            ...filter(
                updatedTags,
                (val) => !_isTagType(val, CATEGORY_TAG_TYPE),
            ),
            tag,
        ];
    }

    if (format || format === '') {
        updatedTags = [
            ...filter(updatedTags, (val) => !_isTagType(val, FORMAT_TAG_TYPE)),
            format,
        ];
    }

    return compact(updatedTags);
};

const _getUpdatedTagsForRemoval = (
    currentTags: string[],
    tag: number,
    format: string,
) =>
    // This approach assues there will only ever be at most one of each type
    // If that is to change than the logic here will need to be updated
    compact(
        currentTags.map((currentTag: string) => {
            if (
                (tag && _isTagType(currentTag, CATEGORY_TAG_TYPE)) ||
                (format && _isTagType(currentTag, FORMAT_TAG_TYPE))
            ) {
                return '';
            }

            return currentTag;
        }),
    );

const _handlePriceUpdate = (price: string, priceInState: string) => {
    let priceValue = priceInState;

    if (price) {
        priceValue = price === CLEAR_PRICE_FILTER ? '' : price;
    }

    return priceValue;
};

const _getUpdatedDates = (dates: string, datesInState: string) => {
    if (dates === CUSTOM_DATE) {
        return CURRENT_FUTURE;
    } else if (dates) {
        return [CURRENT_FUTURE, dates];
    }
    return datesInState;
};

const _getUpdatedDateRange = (
    dates: string,
    startDate?: string,
    endDate?: string,
    dateRangeInState?: StringFlag,
) => {
    if (startDate && endDate) {
        return { from: startDate, to: endDate };
    }
    if (dates) {
        return {};
    }
    return dateRangeInState;
};

const _getUpdatedArrayFilter = (filter: string, filterInState: string) => {
    if (filter) {
        if (typeof filter === 'string') {
            return [filter];
        } else {
            return filter;
        }
    }

    return filterInState;
};

const _getSpecialParams = (filterInState: string, filter?: string) => {
    if (!filter) {
        return filterInState;
    }

    return uniq([...filterInState, filter]);
};

const _getUpdatedPointRadius = (
    pointRadiusInState: PointRadius,
    distance?: string,
    location?: $FixMe,
) => {
    if (distance) {
        //If user is already using their current location,
        //the location will be embedded in pointRadiusInState
        //If not we must grab it from location in state.
        return {
            radius: distance,
            offset: undefined,
            scale: undefined,
            latitude: pointRadiusInState?.latitude || location.latitude,
            longitude: pointRadiusInState?.longitude || location.longitude,
        };
    }

    return pointRadiusInState;
};

interface handleApplyFilterProps {
    tags?: string;
    price?: string;
    dates?: string;
    q?: string;
    format?: string;
    languages?: any;
    currencies?: string;
    special?: string;
    hash?: string;
    distance?: string;
}

export const handleApplyFilter =
    (
        history: object,
        {
            tags,
            price,
            dates,
            q,
            format,
            languages,
            currencies,
            special,
            hash,
            distance,
        }: handleApplyFilterProps,
        { startDate, endDate }: { startDate?: string; endDate?: string } = {},
        shouldUpdateRoute = true,
    ) =>
    (dispatch: Function, getState: Function) => {
        const {
            search: { eventSearch },
            location,
        } = getState();

        const gaTrackingData = getFilterEventTrackingData(
            { tags, price, dates, q, format, languages, currencies, special },
            { startDate, endDate },
        );

        const updateSpecialParams = _getSpecialParams(
            eventSearch.special,
            special,
        );
        const updatedTags = _getUpdatedTags(eventSearch.tags, tags, format);
        const updatedPrice = _handlePriceUpdate(price || '', eventSearch.price);
        const updatedDates = _getUpdatedDates(dates || '', eventSearch.dates);
        const updatedCurrencies = _getUpdatedArrayFilter(
            currencies || '',
            eventSearch.currencies,
        );
        const updatedLanguages = languages || eventSearch.languages;
        const updatedDateRange = _getUpdatedDateRange(
            dates || '',
            startDate,
            endDate,
            eventSearch.dateRange,
        );
        const updatedHash = hash || eventSearch.hash;
        const updatedPointRadius = _getUpdatedPointRadius(
            eventSearch.pointRadius,
            distance,
            location,
        );

        let searchQuery = {
            ...eventSearch,
            q: !(!q && q !== '') ? q : eventSearch.q,
            price: updatedPrice,
            tags: updatedTags,
            dates: updatedDates,
            dateRange: updatedDateRange,
            currencies: updatedCurrencies,
            languages: updatedLanguages,
            special: updateSpecialParams,
            hash: updatedHash,
            pointRadius: updatedPointRadius,
            bbox: distance ? '' : eventSearch.bbox,
            page: 1,
        };

        // when searching for 'events' or 'all events' filters should be resetted
        if (
            includes(
                [EVENTS_TYPE, ALL_EVENTS_TYPE],
                (searchQuery.q || '').toLowerCase(),
            )
        ) {
            searchQuery = {
                ...eventSearch,
                q: '',
                price: '',
                tags: '',
                dates: CURRENT_FUTURE,
                dateRange: '',
                page: 1,
                currencies: '',
                languages: '',
                pointRadius: {},
            };
        }

        dispatch(
            _doNewSearch(
                history,
                searchQuery,
                gaTrackingData,
                shouldUpdateRoute,
            ),
        );
    };

/**
 *
 * @param {*} history
 * @param {*} simpleQuery
 *
 * Takes in a preconfigured query. Resets the current search state to
 * null, and applies the passed in query.
 */
export const handleApplyFilterSimple =
    (
        history: object,
        simpleQuery: {
            tags?: string;
            format?: string;
            dates?: string;
            remainingQuery: any;
        },
    ) =>
    (dispatch: Function, getState: Function) => {
        const {
            search: { eventSearch },
        } = getState();

        const {
            tags = '',
            format = '',
            dates = '',
            ...remainingQuery
        } = simpleQuery;

        const updatedDates = dates ? [CURRENT_FUTURE, dates] : CURRENT_FUTURE;
        const updatedTags = _getUpdatedTags([], tags, format);

        const searchQuery = {
            ...eventSearch,
            q: '',
            price: '',
            dateRange: {},
            currencies: '',
            languages: '',
            page: 1,
            special: [],
            ...{ tags: updatedTags, dates: updatedDates, ...remainingQuery },
        };

        //This doesn't support custom dates. Will need additional work
        const gaTrackingData = getFilterEventTrackingData({ ...searchQuery });

        dispatch(_doNewSearch(history, searchQuery, gaTrackingData));
    };

interface handleRemoveFilterProps {
    tags?: any;
    price?: string;
    dates?: string[];
    format?: string;
    currencies?: string;
    languages?: string;
    special?: string;
    organizationsOr?: string[];
    hash?: string;
    places?: string[];
    distance?: string;
}

const getClearDistanceFilter = (location: any, eventSearch: EventSearch) => {
    const pointRadius = eventSearch.pointRadius;

    //Distance filter will only be cleared if the user is enrolled
    //in the experiment. Given that, if they are sharing their location
    //with us that means the pointRadius filter is holding onto the
    //user's current location.
    const isUsingCurrentLocation = location.isUsingCurrentLocation;

    //If user is sharing their current location remove
    //the distance filter specific radius and reapply
    //the offset/scale to normalize ranking with a standard
    //search.
    if (isUsingCurrentLocation) {
        return {
            ...pointRadius,
            ...SEARCH_RADIUS_DEFAULTS,
        };
    }

    //Handle case where the previous "place" is missing due to
    //an unfound place or other reason, we still want the search
    //to work as expected. So if there is not placeId representation
    //in the location reducer and it is not a map based search
    //we will want to keep using the lat/lng but with a standardized distance
    if (!location.placeId && !eventSearch.bbox && !location.isOnline) {
        return {
            ...pointRadius,
            ...SEARCH_RADIUS_DEFAULTS,
        };
    }

    return {};
};

export const handleRemoveFilter =
    (
        history: object,
        {
            tags,
            price,
            dates,
            format,
            currencies,
            languages,
            special,
            organizationsOr,
            hash,
            distance,
        }: handleRemoveFilterProps,
    ) =>
    (dispatch: Function, getState: Function) => {
        const {
            search: { eventSearch },
            location,
        } = getState();

        const updatedTags = _getUpdatedTagsForRemoval(
            eventSearch.tags,
            tags || '',
            format || '',
        );

        const searchQuery = {
            ...eventSearch,
            tags: updatedTags,
            price: price ? '' : eventSearch.price,
            dates: dates ? CURRENT_FUTURE : eventSearch.dates,
            dateRange: dates ? {} : eventSearch.dateRange,
            currencies: currencies ? '' : eventSearch.currencies,
            languages: languages ? '' : eventSearch.languages,
            organizationsOr: organizationsOr ? [] : eventSearch.organizationsOr,
            hash: hash ? '' : eventSearch.hash,
            special: special ? [] : eventSearch.special,
            page: 1,
            pointRadius: distance
                ? getClearDistanceFilter(location, eventSearch)
                : eventSearch.pointRadius,
        };

        dispatch(_doNewSearch(history, searchQuery));
    };

export const handleRemoveAllFilters =
    (history: object, _args?: any) => (dispatch: Function) => {
        // Since we want to remove only the filters in the filter panel,
        // we don't want to spread ...eventSearch since that would reset
        // the q as well
        const searchQuery = {
            tags: [],
            price: '',
            dates: CURRENT_FUTURE,
            dateRange: {},
            currencies: '',
            languages: '',
            page: 1,
            pointRadius: {},
        };

        dispatch(_doNewSearch(history, searchQuery));
    };
